import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { updateProfile, getAuth } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { db } from '../../lib/firebase';
import { useAuthState, updatePartnerDisplayName as updatePartnerDisplayNameFn } from '../../lib/partnerAuth';
import { useSEO } from '../../hooks/useSEO';
import { DashboardFilters } from '../../components/partners/DashboardFilters';
import {
  DEFAULT_PARTNER_FILTERS,
  computeFilteredStats,
  getInstallsToMinimum,
  getPayoutEligibility,
  getRemainingToMinimum,
  getUnpaidBalance,
  MIN_PAYOUT_USD,
  type PartnerFilterState,
  type ReferralClickRow,
  type ReferralInstallRow,
  type PurchaseRow,
} from '../../lib/partnerStatsFilters';
import { countryCodeToFlag, countryCodeToName } from '../../lib/countryUtils';
import styles from './Dashboard.module.css';

interface PartnerDoc {
  referralCode: string;
  displayName: string;
  totalEarnings: number;
  totalPaid: number;
  status: string;
}

export function PartnerDashboard() {
  const { user } = useAuthState();
  const [partner, setPartner] = useState<(PartnerDoc & { id: string }) | null>(null);
  const [clicks, setClicks] = useState<ReferralClickRow[]>([]);
  const [referrals, setReferrals] = useState<ReferralInstallRow[]>([]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [filters, setFilters] = useState<PartnerFilterState>(DEFAULT_PARTNER_FILTERS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useSEO({ title: 'Partner Dashboard — LuvLust', description: 'Manage your LuvLust referrals and earnings.' });

  useEffect(() => {
    if (!user) return;
    async function load() {
      setLoadError(null);
      try {
        const snap = await getDocs(
          query(collection(db, 'partners'), where('uid', '==', user!.uid), limit(1)),
        );
        if (snap.empty) {
          setPartner(null);
          return;
        }
        const doc = snap.docs[0];
        const data = doc.data() as PartnerDoc;
        setPartner({ id: doc.id, ...data });

        const [clickSnap, referralSnap, purchaseSnap] = await Promise.all([
          getDocs(query(collection(db, 'referral_clicks'), where('partnerId', '==', doc.id))),
          getDocs(collection(db, `partners/${doc.id}/referrals`)),
          getDocs(collection(db, `partners/${doc.id}/purchases`)),
        ]);

        setClicks(
          clickSnap.docs.map((d) => {
            const row = d.data();
            return {
              platform: row.platform as string | undefined,
              clickedAt: row.clickedAt,
              campaignId: row.campaignId as string | undefined,
            };
          }),
        );
        setReferrals(
          referralSnap.docs.map((d) => {
            const row = d.data();
            return {
              platform: row.platform as string | undefined,
              installedAt: row.installedAt,
              method: row.method as string | undefined,
              deviceId: row.deviceId as string | undefined,
              campaignId: row.campaignId as string | undefined,
              country: row.country as string | undefined,
            };
          }),
        );
        setPurchases(
          purchaseSnap.docs.map((d) => {
            const row = d.data();
            return {
              platform: row.platform as string | undefined,
              purchasedAt: row.purchasedAt,
              deviceId: row.deviceId as string | undefined,
              campaignId: row.campaignId as string | undefined,
            };
          }),
        );
      } catch (e) {
        console.error('Partner dashboard load failed:', e);
        setLoadError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  const filtered = useMemo(
    () => computeFilteredStats(clicks, referrals, purchases, filters),
    [clicks, referrals, purchases, filters],
  );

  const hasActiveFilters =
    filters.platform !== 'all' || Boolean(filters.dateFrom || filters.dateTo);

  if (loading) return <div className={styles.loading}>Loading dashboard...</div>;
  if (loadError) {
    return (
      <div className={styles.loading}>
        Could not load dashboard: {loadError}
        <p style={{ marginTop: 16, fontSize: 14, opacity: 0.85 }}>
          Try refreshing the page. If this persists, contact support.
        </p>
      </div>
    );
  }
  if (!partner) {
    return (
      <div className={styles.loading}>
        Partner account not found. Please contact support.
      </div>
    );
  }

  const partnerData = partner;
  const referralLink = `https://luvlustapp.com/r/${partnerData.referralCode}`;
  const unpaid = getUnpaidBalance(partnerData.totalEarnings, partnerData.totalPaid);
  const eligibility = getPayoutEligibility(unpaid);
  const remainingToMin = getRemainingToMinimum(unpaid);
  const installsToMin = getInstallsToMinimum(unpaid);
  const payoutProgress = Math.min(100, (unpaid / MIN_PAYOUT_USD) * 100);

  function copyLink() {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function startEditName() {
    setNameDraft(partnerData.displayName);
    setNameError(null);
    setEditingName(true);
  }

  function cancelEditName() {
    setEditingName(false);
    setNameError(null);
  }

  async function saveDisplayName() {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameError('Name cannot be empty');
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const result = await updatePartnerDisplayNameFn(trimmed);
      const authUser = getAuth().currentUser;
      if (authUser) {
        await updateProfile(authUser, { displayName: result.displayName });
      }
      setPartner((prev) => (prev ? { ...prev, displayName: result.displayName } : prev));
      setEditingName(false);
    } catch (err: unknown) {
      if (err instanceof FirebaseError && err.message) {
        setNameError(err.message);
      } else {
        setNameError('Could not update name');
      }
    } finally {
      setNameSaving(false);
    }
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.greetingRow}>
        {editingName ? (
          <div className={styles.nameEditRow}>
            <input
              className={styles.nameInput}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={40}
              disabled={nameSaving}
              aria-label="Partner display name"
            />
            <button
              type="button"
              className={styles.nameSaveBtn}
              disabled={nameSaving}
              onClick={() => void saveDisplayName()}
            >
              {nameSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className={styles.nameCancelBtn}
              disabled={nameSaving}
              onClick={cancelEditName}
            >
              Cancel
            </button>
            {nameError ? <p className={styles.nameEditError}>{nameError}</p> : null}
          </div>
        ) : (
          <>
            <h1 className={styles.greeting}>Welcome back, {partnerData.displayName}</h1>
            <button type="button" className={styles.editNameBtn} onClick={startEditName}>
              Edit name
            </button>
          </>
        )}
      </div>

      <div className={styles.linkCard}>
        <div className={styles.linkStrip}>
          <span className={styles.linkTitle}>Your Referral Link</span>
          <code className={styles.link}>{referralLink}</code>
          <button type="button" className={styles.copyBtn} onClick={copyLink}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <span className={styles.linkHint}>
            Share this link with your followers. They&apos;ll be redirected to download LuvLust.
          </span>
        </div>
      </div>

      <DashboardFilters filters={filters} onChange={setFilters} />

      <div className={styles.sectionHeadingRow}>
        <h2 className={styles.sectionHeading}>Performance</h2>
        {hasActiveFilters ? (
          <span className={styles.sectionHeadingHint}>Reflecting your selected filters</span>
        ) : null}
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{filtered.clicks}</div>
          <div className={styles.statLabel}>Link Clicks</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{filtered.installs}</div>
          <div className={styles.statLabel}>Installs</div>
          {filters.platform === 'all' ? (
            <div className={styles.statPlatformRow}>
              <span>iOS {filtered.installsIos}</span>
              <span className={styles.statPlatformSep}>·</span>
              <span>Android {filtered.installsAndroid}</span>
            </div>
          ) : null}
          {filtered.clicks > 0 ? (
            <div className={styles.statSubtext}>
              {((filtered.installs / filtered.clicks) * 100).toFixed(1)}% of clicks
            </div>
          ) : null}
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{filtered.purchases}</div>
          <div className={styles.statLabel}>Purchases</div>
          {filters.platform === 'all' ? (
            <div className={styles.statPlatformRow}>
              <span>iOS {filtered.purchasesIos}</span>
              <span className={styles.statPlatformSep}>·</span>
              <span>Android {filtered.purchasesAndroid}</span>
            </div>
          ) : null}
          {filtered.installs > 0 ? (
            <div className={styles.statSubtext}>
              {((filtered.purchases / filtered.installs) * 100).toFixed(1)}% of installs
            </div>
          ) : null}
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--accent)' }}>
            ${partnerData.totalEarnings.toFixed(2)}
          </div>
          <div className={styles.statLabel}>Total Earned</div>
          <div className={styles.statSubtext}>$0.10 per install</div>
        </div>
      </div>

      <h2 className={styles.sectionHeading}>Payouts</h2>

      <div className={styles.earningsRow}>
        <div className={styles.earningsCard}>
          <span className={styles.earningsLabel}>Total Paid</span>
          <span className={styles.earningsValue}>${partnerData.totalPaid.toFixed(2)}</span>
        </div>
        <div className={`${styles.earningsCard} ${styles.unpaidCard}`}>
          <span className={styles.earningsLabel}>Unpaid Balance</span>
          <span
            className={styles.earningsValue}
            style={{ color: unpaid > 0 ? 'var(--accent)' : 'var(--text-muted)' }}
          >
            ${unpaid.toFixed(2)}
          </span>
          <span className={styles.earningsSubtext}>${MIN_PAYOUT_USD} minimum payout</span>
          {eligibility === 'accumulating' ? (
            <>
              <div className={styles.payoutProgressTrack}>
                <div
                  className={styles.payoutProgressFill}
                  style={{ width: `${payoutProgress}%` }}
                />
              </div>
              <span className={styles.earningsHint}>
                ${remainingToMin.toFixed(2)} to go · ~{installsToMin} more install
                {installsToMin === 1 ? '' : 's'}
              </span>
            </>
          ) : null}
          {eligibility === 'eligible' ? (
            <span className={styles.eligibleHint}>Eligible for next payout</span>
          ) : null}
        </div>
      </div>

      {Object.keys(filtered.countryBreakdown).length > 0 && (
        <div className={styles.linkCard}>
          <h2 className={styles.cardTitle}>Top Countries</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(filtered.countryBreakdown)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10)
              .map(([code, count]) => (
                <div
                  key={code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: 8,
                  }}
                >
                  <span style={{ fontSize: 14 }}>
                    {countryCodeToFlag(code)} {countryCodeToName(code)}
                  </span>
                  <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>
                    {count} {count === 1 ? 'install' : 'installs'}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}